import React, { useState, useEffect } from "react";
import {
    Avatar,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Backdrop,
    CircularProgress,
    Link,
    Grid,
    Card,
    CardContent,
    CardActions,
    Chip,
    Container,
    Paper,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import { 
    PhotoCamera, 
    CheckCircle,
    EditIcon,
    AddCircleOutlineIcon,
    Email,
    Phone,
    Payment,
    Person,
    Language,
    Edit,
    Add,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import API from "../../../API";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import UploadFileDialog from "../../../Components/tools/uploadfile";
import routes from "../../../Config/routes";
import { parseLocalFloat, parseLocalInt } from "../../../Utils/constants";
import CreatorInfo from "../../../Components/CreatorData/creatorInfo";
import { toast } from 'sonner';
import PlatformDialog from "../../../Components/CreatorData/creatorPlatforms";

// Platform configurations
const PLATFORM_CONFIGS = {
  Instagram: {
    color: '#E4405F',
    prefix: 'instagram.com/',
    emoji: '📸',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 },
      { key: 'feedRate', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'storyRate', label: 'Story Rate', type: 'rate', minValue: 20 }
    ]
  },
  TikTok: {
    color: '#000000',
    prefix: 'tiktok.com/@',
    emoji: '🎵',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'brandRate', label: 'Brand Rate', type: 'rate', minValue: 100 },
      { key: 'songRate', label: 'Song Rate', type: 'rate', minValue: 100 }
    ]
  },
  YouTube: {
    color: '#FF0000',
    prefix: 'youtube.com/',
    emoji: '▶️',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Subscriber Count', type: 'number' },
      { key: 'integrationRate', label: 'Brand Integration Rate', type: 'rate', minValue: 100 },
      { key: 'shortsRate', label: 'Shorts Rate', type: 'rate', minValue: 100 },
      { key: 'dedicatedRate', label: 'Dedicated Video Rate', type: 'rate', minValue: 100 }
    ]
  },
  X: {
    color: '#000000',
    prefix: 'x.com/',
    emoji: '𝕏',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'feedPost', label: 'Feed Post Rate', type: 'rate', minValue: 100 },
      { key: 'repost', label: 'Repost Rate', type: 'rate', minValue: 100 }
    ]
  },
  Podcast: {
    color: '#9C27B0',
    prefix: '',
    emoji: '🎙️',
    isUrl: true,
    fields: [
      { key: 'followerCount', label: 'Listener Count', type: 'number' },
      { key: 'preroll', label: 'Preroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'midroll', label: 'Midroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'postroll', label: 'Postroll Ad Rate', type: 'rate', minValue: 100 },
      { key: 'full', label: 'Full Episode Sponsorship Rate', type: 'rate', minValue: 100 }
    ]
  },
  Kick: {
    color: '#53FC18',
    prefix: 'kick.com/',
    emoji: '🎮',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  },
  Twitch: {
    color: '#9146FF',
    prefix: 'twitch.tv/',
    emoji: '🟣',
    isUrl: false,
    fields: [
      { key: 'followerCount', label: 'Follower Count', type: 'number' },
      { key: 'placement', label: 'Product Placement Rate', type: 'rate', minValue: 100 }
    ]
  }
};

// Build API payload for specific platform
const buildPlatformPayload = (platform, data) => {
  const payload = {};
 
  switch(platform) {
    case 'Instagram':
      payload.instagram = parseLocalInt(data.followerCount) || null;
      payload.instagram_link = data.link ? `https://www.instagram.com/${data.link}` : null;
      payload.rate_ig_reelbrand = parseLocalFloat(data.brandRate);
      payload.rate_ig_reelsound = parseLocalFloat(data.songRate);
      payload.rate_ig_feedpost = parseLocalFloat(data.feedRate);
      payload.rate_ig_story = parseLocalFloat(data.storyRate);
      break;
    case 'TikTok':
      payload.tiktok = parseLocalInt(data.followerCount) || null;
      payload.tiktok_link = data.link ? `https://www.tiktok.com/@${data.link}` : null;
      payload.rate_tt_brand = parseLocalFloat(data.brandRate);
      payload.rate_tt_sound = parseLocalFloat(data.songRate);
      break;
    case 'YouTube':
      payload.youtube = parseLocalInt(data.followerCount) || null;
      payload.youtube_link = data.link ? `https://www.youtube.com/${data.link}` : null;
      payload.rate_yt_integ = parseLocalFloat(data.integrationRate);
      payload.rate_yt_short = parseLocalFloat(data.shortsRate);
      payload.rate_yt_dedicated = parseLocalFloat(data.dedicatedRate);
      break;
    case 'X':
      payload.follow_x = parseLocalInt(data.followerCount) || null;
      payload.x_link = data.link ? `https://www.x.com/${data.link}` : null;
      payload.rate_x_feedpost = parseLocalFloat(data.feedPost);
      payload.rate_x_repost = parseLocalFloat(data.repost);
      break;
    case 'Podcast':
      payload.follow_podcast = parseLocalInt(data.followerCount) || null;
      payload.podcast_link = data.link || null;
      payload.rate_podcast_preroll = parseLocalFloat(data.preroll);
      payload.rate_podcast_midroll = parseLocalFloat(data.midroll);
      payload.rate_podcast_postroll = parseLocalFloat(data.postroll);
      payload.rate_podcast_full = parseLocalFloat(data.full);
      break;
    case 'Kick':
      payload.follow_kick = parseLocalInt(data.followerCount) || null;
      payload.kick_link = data.link ? `https://kick.com/${data.link}` : null;
      payload.rate_kick_product = parseLocalFloat(data.placement);
      break;
    case 'Twitch':
      payload.twitch = parseLocalInt(data.followerCount) || null;
      payload.twitch_link = data.link ? `https://www.twitch.tv/${data.link}` : null;
      payload.rate_twitch_product = parseLocalFloat(data.placement);
      break;
  }
 
  return payload;
};

/** ---------- SECTION COMPONENT ---------- */
const SettingsSection = ({ title, icon, children }) => {
  const theme = useTheme();
  
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ 
          color: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

export default function AgencyCreatorSettings({ creatorData, onUpdate }) {
    const theme = useTheme();
    const { dialogState, openDialog, closeDialog } = useAlertDialog();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [contactEmail, setContactEmail] = useState('');
    const [paymentEmail, setPaymentEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [creatorName, setCreatorName] = useState('');
    const [creatorRegion, setCreatorRegion] = useState('');
    const [primaryMarket, setPrimaryMarket] = useState([]);
    const [notesContentStyle, setNotesContentStyle] = useState('');
    const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
    const [race, setRace] = useState('');
    const [gender, setGender] = useState('');
    const [location, setLocation] = useState('');

    const [userStatus, setUserStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("PayPal");
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [processEmails, setProcessEmails] = useState(true);
    const [forwardedAlias, setForwardedAlias] = useState('');

    // Platform data storage
    const [platformData, setPlatformData] = useState({});
    
    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [dialogData, setDialogData] = useState({});
    const [dialogSaving, setDialogSaving] = useState(false);

    useEffect(() => {
        if (creatorData) {
            GetUserData();
        }
    }, [creatorData])

    function GetUserData() {
        const user = creatorData.creator_user;
        const creator = creatorData.creator;

        setUserStatus(creator?.status ? creator.status : (user?.refresh_token ? 'Partner' : 'Associate'));
        setPaymentMethod(user?.payout_preferred || 'PayPal');

        setContactEmail(creator.email);
        setPaymentEmail(user?.paypal || creator.payout_email || '');
        setPhoneNumber(creator.phone_number || '');
        setCreatorName(creator.display_name || creator.creator);
        setCreatorRegion(creator.region || '');
        setPrimaryMarket(creator.primary_markets || []);
        setNotesContentStyle(creator.notes_content_style || '');
        setRace(creator.ethnicity || '');
        setGender(creator.gender || '');
        setLocation(creator.geolocation || '');
        setProfilePhotoUrl(creator.pfphref);
        setProcessEmails(user?.process_emails ?? true);
        setForwardedAlias(user?.forwarded_alias || '');

        // Build platform data object
        const data = {
            Instagram: {
                link: creator.instagram_link?.replace("https://www.instagram.com/", "") || "",
                followerCount: creator.instagram ? creator.instagram.toString() : "",
                brandRate: creator.rate_ig_reelbrand?.toFixed(2) || "",
                songRate: creator.rate_ig_reelsound?.toFixed(2) || "",
                feedRate: creator.rate_ig_feedpost?.toFixed(2) || "",
                storyRate: creator.rate_ig_story?.toFixed(2) || "",
                active: !!creator.instagram_link
            },
            TikTok: {
                link: creator.tiktok_link?.replace("https://www.tiktok.com/@", "") || "",
                followerCount: creator.tiktok ? creator.tiktok.toString() : "",
                brandRate: creator.rate_tt_brand?.toFixed(2) || "",
                songRate: creator.rate_tt_sound?.toFixed(2) || "",
                active: !!creator.tiktok_link
            },
            YouTube: {
                link: creator.youtube_link?.replace("https://www.youtube.com/${data.link}") || "",
                followerCount: creator.youtube ? creator.youtube.toString() : "",
                integrationRate: creator.rate_yt_integ?.toFixed(2) || "",
                shortsRate: creator.rate_yt_short?.toFixed(2) || "",
                dedicatedRate: creator.rate_yt_dedicated?.toFixed(2) || "",
                active: !!creator.youtube_link
            },
            X: {
                link: creator.x_link?.replace("https://x.com/", "") || "",
                followerCount: creator.follow_x ? creator.follow_x.toString() : "",
                feedPost: creator.rate_x_feedpost?.toFixed(2) || "",
                repost: creator.rate_x_repost?.toFixed(2) || "",
                active: !!creator.x_link
            },
            Podcast: {
                link: creator.podcast_link || "",
                followerCount: creator.follow_podcast ? creator.follow_podcast.toString() : "",
                preroll: creator.rate_podcast_preroll?.toFixed(2) || "",
                midroll: creator.rate_podcast_midroll?.toFixed(2) || "",
                postroll: creator.rate_podcast_postroll?.toFixed(2) || "",
                full: creator.rate_podcast_full?.toFixed(2) || "",
                active: !!creator.podcast_link
            },
            Kick: {
                link: creator.kick_link?.replace("https://kick.com/", "") || "",
                followerCount: creator.follow_kick ? creator.follow_kick.toString() : "",
                placement: creator.rate_kick_product?.toFixed(2) || "",
                active: !!creator.kick_link
            },
            Twitch: {
                link: creator.twitch_link?.replace("https://www.twitch.tv/", "") || "",
                followerCount: creator.twitch ? creator.twitch.toString() : "",
                placement: creator.rate_twitch_product?.toFixed(2) || "",
                active: !!creator.twitch_link
            }
        };
        
        setPlatformData(data);
    }

    const handleOpenUploadDialog = () => {
        setIsUploadDialogOpen(true);
    };

    const handleOpenDialog = (platform) => {
        setSelectedPlatform(platform);
        setDialogData(platformData[platform] || {});
        setDialogOpen(true);
    };
    
    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedPlatform(null);
        setDialogData({});
        setDialogSaving(false);
    };
    
    const handleSavePlatform = async (platform, data) => {
        try {
            setDialogSaving(true);
            
            const payload = buildPlatformPayload(platform, data);
            
            await API.agency.editCreator(creatorData.creator.creator, payload);
            
            // Update local state
            setPlatformData(prev => ({
                ...prev,
                [platform]: { ...data, active: true }
            }));
            
            toast.success(`${platform} updated successfully`);
            handleCloseDialog();
            
            // Call parent update callback
            if (onUpdate) {
                onUpdate();
            }
            
        } catch (err) {
            const errorMsg = err?.response?.data?.error || err?.response?.data?.details || err.message;
            openDialog("Error", `Failed to update ${platform}: ${errorMsg}`, closeDialog, closeDialog, "Ok", null);
        } finally {
            setDialogSaving(false);
        }
    };

    const handleSaveCreator = async () => {
        if(!creatorName){
            toast.error('Display name is required');
            return;
        }

        if (phoneNumber && phoneNumber.length < 11) {
            toast.error('Phone number must be at least 11 digits');
            return;
        }

        if (creatorName.includes(" ")) {
            setValidationError("Creator name cannot contain spaces.");
            toast.error("Creator name cannot contain spaces");
            return;
        }

        setIsLoading(true);

        let payload = null;

        try {
            payload = {
                geolocation: location,
                gender: gender,
                ethnicity: race,
                primary_markets: primaryMarket,
                region: creatorRegion,
                notescontent_style: notesContentStyle || null,
                contactEmail: contactEmail,
                paymentEmail: paymentEmail,
                phone_number: phoneNumber,
                pfphref: profilePhotoUrl,
                display_name: creatorName,
                process_emails : processEmails,
                forwarded_alias : processEmails ? forwardedAlias : null
            };
        } catch (error) {
            toast.error(`Error parsing form: ${error}`);
            setIsLoading(false);
            return;
        }

        try {
            await API.agency.editCreator(creatorData.creator.creator, payload);
            toast.success('Creator data updated successfully');
            
            // Call parent update callback
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;
            toast.error(`Failed to update: ${errorMsg}`);
            console.error('Network error:', error);
        }
        setIsLoading(false);
    };

    const platformKeys = Object.keys(PLATFORM_CONFIGS);

    if (!creatorData) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    const isVerified = !!creatorData?.creator_user;

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <AlertDialog alertState={dialogState} />
            
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Profile Photo Section */}
                <SettingsSection title="Profile Photo" icon={<Person />}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Avatar
                            alt={creatorName}
                            src={profilePhotoUrl}
                            sx={{ width: 120, height: 120, cursor: 'pointer', boxShadow: 2 }}
                            onClick={handleOpenUploadDialog}
                        >
                            {!profilePhotoUrl && <PhotoCamera fontSize="large" />}
                        </Avatar>
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={<PhotoCamera />}
                                onClick={handleOpenUploadDialog}
                            >
                                Upload Photo
                            </Button>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                Recommended: Square image, at least 400x400px
                            </Typography>
                        </Box>
                    </Stack>
                </SettingsSection>

                {/* Verification Status */}
                <SettingsSection title="Verification Status" icon={<CheckCircle />}>
                    <Stack spacing={2}>
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: isVerified ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                            borderRadius: 1,
                            border: 1,
                            borderColor: isVerified ? theme.palette.success.main : theme.palette.warning.main
                        }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <CheckCircle 
                                    sx={{ color: isVerified ? 'success.main' : 'action.disabled', fontSize: 32 }} 
                                />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {isVerified ? 'Verified Partner' : 'Unverified Associate'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {isVerified 
                                            ? 'This creator has completed verification and has full platform access'
                                            : 'This creator needs to verify their account to become a partner'
                                        }
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                        {!isVerified && (
                            <Link 
                                component={RouterLink} 
                                to={routes.creatorConnect.replace(':creator', creatorData?.creator?.creator)}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                <Button variant="contained" color="primary">
                                    Send Verification Link
                                </Button>
                            </Link>
                        )}
                    </Stack>
                </SettingsSection>

                {/* Payment Information */}
                <SettingsSection title="Payment Information" icon={<Payment />}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled>
                                <InputLabel>Payment Method</InputLabel>
                                <Select value={paymentMethod} label='Payment Method'>
                                    <MenuItem value="PayPal">PayPal, Standard, Fees Apply</MenuItem>
                                    <MenuItem value="Stripe">BlitzPay, Faster, Less Fees</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="PayPal Payment Email"
                                type="email"
                                disabled={paymentMethod !== 'PayPal'}
                                fullWidth
                                value={paymentEmail}
                                onChange={(e) => setPaymentEmail(e.target.value)}
                                required
                            />
                        </Grid>
                    </Grid>
                </SettingsSection>

                {/* Contact Information */}
                <SettingsSection title="Contact Information" icon={<Email />}>
                    <CreatorInfo
                        creatorName={creatorName}
                        setCreatorName={setCreatorName}
                        creatorRegion={creatorRegion}
                        setCreatorRegion={setCreatorRegion}
                        race={race}
                        setRace={setRace}
                        gender={gender}
                        setGender={setGender}
                        location={location}
                        setLocation={setLocation}
                        primaryMarket={primaryMarket}
                        setPrimaryMarket={setPrimaryMarket}
                        notesContentStyle={notesContentStyle}
                        setNotesContentStyle={setNotesContentStyle}
                        contactEmail={contactEmail}
                        setContactEmail={setContactEmail}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        validationError={validationError}
                    />
                </SettingsSection>
                    
                {/* Platform and Rates */}
                <SettingsSection title="Platforms & Rates" icon={<Language />}>
                    <Grid container spacing={2}>
                        {platformKeys.map((platform) => {
                            const config = PLATFORM_CONFIGS[platform];
                            const data = platformData[platform] || {};
                            const isActive = data.active;
                           
                            return (
                                <Grid item xs={12} sm={6} md={4} key={platform}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 2,
                                            border: isActive ? `2px solid ${config.color}` : '2px solid #e0e0e0',
                                            boxShadow: isActive ? 3 : 1,
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                boxShadow: 4,
                                                transform: 'translateY(-2px)'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar sx={{
                                                        bgcolor: config.color,
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: '1.25rem'
                                                    }}>
                                                        {config.emoji}
                                                    </Avatar>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {platform}
                                                        </Typography>
                                                        {isActive && (
                                                            <Chip
                                                                label="Active"
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: alpha(config.color, 0.15),
                                                                    color: config.color,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Stack>
                                               
                                                {isActive ? (
                                                    <>
                                                        {data.followerCount && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Followers
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {parseInt(data.followerCount).toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {data.link && (
                                                            <Typography 
                                                                variant="caption" 
                                                                color="text.secondary"
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {config.prefix}{data.link}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Not configured
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </CardContent>
                                       
                                        <CardActions sx={{ pt: 0, pb: 1.5, px: 2 }}>
                                            <Button
                                                fullWidth
                                                size="small"
                                                variant={isActive ? "outlined" : "contained"}
                                                startIcon={isActive ? <Edit /> : <Add />}
                                                onClick={() => handleOpenDialog(platform)}
                                                sx={{
                                                    borderColor: config.color,
                                                    color: isActive ? config.color : 'white',
                                                    bgcolor: isActive ? 'transparent' : config.color,
                                                    '&:hover': {
                                                        bgcolor: isActive ? alpha(config.color, 0.08) : undefined,
                                                        borderColor: config.color
                                                    }
                                                }}
                                            >
                                                {isActive ? 'Edit' : 'Add'}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </SettingsSection>

                {/* Email Forwarding */}
                <SettingsSection title="Email Forwarding" icon={<Email />}>
                    <Stack spacing={2}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={processEmails}
                                        onChange={(e) => setProcessEmails(e.target.checked)}
                                    />
                                }
                                label="Enable automatic email forwarding"
                            />
                        </FormGroup>
                        <TextField
                            label="Forwarding Alias Email"
                            type="email"
                            fullWidth
                            disabled={!processEmails}
                            value={forwardedAlias}
                            onChange={(e) => setForwardedAlias(e.target.value)}
                            helperText={processEmails ? "Emails will be forwarded to this address" : "Enable forwarding to use this feature"}
                        />
                    </Stack>
                </SettingsSection>

                {/* Save Button */}
                <Paper sx={{ p: 3, position: 'sticky', bottom: 0, zIndex: 10 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={isLoading}
                        onClick={handleSaveCreator}
                        fullWidth
                        sx={{ maxWidth: 300 }}
                    >
                        {isLoading ? 'Saving...' : 'Save All Changes'}
                    </Button>
                </Paper>
            </Container>
            
            {/* Upload File Dialog */}
            <UploadFileDialog
                open={isUploadDialogOpen}
                onClose={() => setIsUploadDialogOpen(false)}
                onUploadSuccess={(data) => {
                    if (data?.url) {
                        setProfilePhotoUrl(data.url);
                        toast.success('Profile photo updated');
                    }
                    setIsUploadDialogOpen(false);
                }}
            />
            
            {/* Platform Dialog */}
            {selectedPlatform && dialogOpen && (
            <PlatformDialog
                open={dialogOpen}
                platform={selectedPlatform}
                config={PLATFORM_CONFIGS[selectedPlatform]}
                data={dialogData}
                saving={dialogSaving}
                onClose={handleCloseDialog}
                onSave={handleSavePlatform}
                // Add these props to make it mobile-friendly
                fullScreen={isMobile}
                PaperProps={{
                sx: {
                    maxHeight: isMobile ? '100vh' : '90vh',
                }
                }}
            />
            )}
        </>
    );
}